import {
  IconBuilding,
  IconBrain,
  IconUser,
  IconDeviceLaptop,
  IconUsers,
  IconReport,
  IconTools,
  IconBuildingWarehouse,
  IconTruck,
  IconRulerMeasure,
  IconCalendar,
  IconClock,
  IconCheckbox,
  IconNumber123,
  IconEye,
  IconBuildingSkyscraper,
  IconChecklist,
  IconHome,
  IconPigMoney,
  IconBrandProducthunt,
  IconChalkboard,
  IconClipboard,
  IconStar,
  IconDashboard,
  IconSettings,
} from '@tabler/icons-react';
import { useUserContext } from '../../context/user_context/UserContext';
import { useOrganizationContext } from '../../context/organization_context/OrganizationContext';
import { StringKeys } from '../../resources/strings/StringKeys';
import { PointOfSale } from '@mui/icons-material';

const useNavBarItems = () => {
  const { firebaseUser, userRole } = useUserContext();
  const { organization, organizationRoles, timeKeepingMethod } = useOrganizationContext();
  const linkColor = "#38ace7";

  if (!organization || !firebaseUser || !userRole || !organizationRoles) return [];

  const rolePermissions = organizationRoles[userRole] || {};
  
  // Function to check if a module has read access
  const hasReadAccess = (module) => {
    if (!module) return false;
    const key = module.replace(/\//g, '').replaceAll(' ', '_').toLowerCase();
    return rolePermissions[key]?.read || 
      rolePermissions[key]?.read_all_reviews || 
      rolePermissions[key]?.read_and_write_all_reviews || 
      rolePermissions[key]?.read_and_write_reviews_for_managed_employees ||
      rolePermissions[key]?.read_all_time_clocks ||
      rolePermissions[key]?.read_and_write_all_time_clocks ||
      rolePermissions[key]?.read_and_write_time_clocks_for_managed_employees ||
      false;
  };

  // Define all menu items with their complete structure
  const items = [
    { label: 'Home', route: '/home', translation_key: StringKeys.HOME, icon: <IconHome style={{ color: linkColor }} /> },
    { label: organization.name, translation_key: StringKeys.ORGANIZATION, route: '/organization', icon: <IconBuilding style={{ color: linkColor }} /> },
    { label: 'Assets', translation_key: StringKeys.ASSETS, route: '/assets', icon: <IconDeviceLaptop style={{ color: linkColor }} /> },
    { label: 'BiziBoard', translation_key: StringKeys.BIZIBOARD, route: '/biziboard', icon: <IconChalkboard style={{ color: linkColor }} /> },
    { label: 'BiziDocs', translation_key: StringKeys.BIZIDOCS, route: '/bizidocs', icon: <IconBrain style={{ color: linkColor }} /> },
    { label: 'Calendar', translation_key: StringKeys.CALENDAR, route: '/calendar', icon: <IconCalendar style={{color: linkColor}}/> },
    { label: 'Customers', translation_key: StringKeys.CUSTOMERS, route: '/customers', icon: <IconUser style={{ color: linkColor }} /> },
    { label: 'Locations', translation_key: StringKeys.LOCATIONS, route: '/locations', icon: <IconBuildingWarehouse style={{ color: linkColor }} /> },    
    { label: 'Roles', translation_key: StringKeys.ROLES, route: '/roles', icon: <IconChecklist style={{ color: linkColor }} /> },
    {
      label: 'Employees',
      translation_key: StringKeys.EMPLOYEES,
      subItems: [
        { label: 'Employees', translation_key: StringKeys.EMPLOYEES, route: '/employees', icon: <IconUsers style={{ color: linkColor }} /> },
        { label: 'Departments', translation_key: StringKeys.DEPARTMENTS, route: '/departments', icon: <IconBuildingSkyscraper style={{ color: linkColor }} /> },
        { label: 'Performance Reviews', translation_key: StringKeys.PERFORMANCE, route: '/performance-reviews', icon: <IconStar style={{ color: linkColor }} /> }
      ]
    },
    {
      label: 'Inventory',
      translation_key: StringKeys.INVENTORY,
      subItems: [
        { label: 'Inventory', translation_key: StringKeys.INVENTORY, route: '/inventory', icon: <IconTools style={{ color: linkColor }} /> },
        { label: 'Inventory Orders', translation_key: StringKeys.ORDERS, route: '/inventory-orders', icon: <IconClipboard style={{ color: linkColor }} /> },
        { label: 'Suppliers', translation_key: StringKeys.SUPPLIERS, route: '/suppliers', icon: <IconTruck style={{ color: linkColor }} /> },
        { label: 'Unit of Measure', translation_key: StringKeys.UNIT_OF_MEASURE, route: '/unit-of-measure', icon: <IconRulerMeasure style={{ color: linkColor }} /> }
      ]
    },
    {
      label: 'Invoices',
      translation_key: StringKeys.INVOICES,
      subItems: [
        { label: 'Invoice Dashboard', translation_key: StringKeys.INVOICE_DASHBOARD, route: '/invoice-dashboard', icon: <IconDashboard style={{ color: linkColor }} /> },
        { label: 'Invoices', translation_key: StringKeys.INVOICES, route: '/invoices', icon: <IconPigMoney style={{ color: linkColor }} /> },
        { label: 'Products', translation_key: StringKeys.PRODUCTS, route: '/products', icon: <IconBrandProducthunt style={{ color: linkColor }} /> },
        { label: 'Invoice Settings', translation_key: StringKeys.INVOICE_SETTINGS, route: '/invoice-settings', icon: <IconSettings style={{ color: linkColor }} /> }
      ]
    },
    {
      label: 'Time Tracking',
      translation_key: StringKeys.TIME_TRACKING,
      subItems: [
        ...(timeKeepingMethod === 'Time Clock' ? [
          { label: 'Time Clock', translation_key: StringKeys.TIME_CLOCK, route: '/time-clock', icon: <IconClock style={{ color: linkColor }} /> },
          { label: 'Review Time Clocks', translation_key: StringKeys.REVIEW_TIME_CLOCKS, route: '/review-time-clocks', icon: <IconEye style={{ color: linkColor }} /> }
        ] : [
          { label: 'Review Time Sheets', translation_key: StringKeys.REVIEW_TIME_SHEETS, route: '/review-time-sheets', icon: <IconCheckbox style={{ color: linkColor }} /> },
          { label: 'Time Sheets', translation_key: StringKeys.TIME_SHEET, route: '/time-sheets', icon: <IconReport style={{ color: linkColor }} /> },
          { label: 'Job Codes', translation_key: StringKeys.JOB_CODES, route: '/job-codes', icon: <IconNumber123 style={{ color: linkColor }} /> }
        ])
      ]
    }
  ];

  // Process menu items to apply permissions logic
  const processedItems = items.map(item => {
    // For items without subitems, simply check if they have read permissions
    if (!item.subItems) {
      return {
        ...item,
        isAccessible: (item.route === '/home' || item.route === '/organization') ? true : hasReadAccess(item.label)
      };
    }
    
    // For items with subitems, filter subitems based on permissions
    const accessibleSubItems = item.subItems.filter(subItem => hasReadAccess(subItem.label));
    
    // Item is accessible if ANY of its subitems are accessible
    const isParentAccessible = accessibleSubItems.length > 0;
    
    return {
      ...item,
      subItems: accessibleSubItems,
      isAccessible: isParentAccessible
    };
  });

  // Return only accessible items
  return processedItems.filter(item => item.isAccessible);
};

export default useNavBarItems;
